import { useCallback, useState, useEffect, useMemo } from 'react';
import { useTranslate, useInfiniteGetList } from 'react-admin';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import { ChatPanel as ChatPanelUI } from '../../../components/chat/ChatPanel/ChatPanel';
import { MessageComposer } from '../../../components/chat/ChatPanel/MessageComposer';
import { ForwardMessageDialog } from '../../../components/chat/ForwardMessageDialog';
import { EmptyConversationState } from '../../../components/chat/EmptyConversationState';
import { useConversationSocket, useTypingIndicatorEnhanced, useSendMessage, useRetryMessage } from '../../../websocket/hooks';
import { ChannelType, TicketStatus, type Conversation, type Message, type WsContactUpdatedPayload } from '../../../types/chat.types';
import { ConnectionEvent, ContactEvent } from '../../../types/websocket-events';
import { wsClient } from '../../../websocket/WebSocketClient';

interface ChatPanelProps {
  /** Selected conversation to display */
  conversation: Conversation | null;
  /** Whether conversation is loading */
  isLoading?: boolean;
  /** Callback to toggle details panel */
  onToggleDetails?: () => void;
  /** Whether details panel is open */
  isDetailsOpen?: boolean;
  /** Callback after a message is successfully forwarded */
  onForwardSuccess?: (conversationId: number) => void;
}

/**
 * ChatPanel - Center panel for ticket chat messages and composer
 *
 * Architecture:
 * - WebSocket is the SOURCE OF TRUTH for messages
 * - API is used ONLY for initial message load (scrollback history)
 * - After initial load, all new messages come from WebSocket
 * - Auto-reconnect is handled by WebSocketClient
 *
 * Features:
 * - Displays chat header with contact/ticket info
 * - Shows message thread with virtualization support
 * - Includes message composer for sending messages
 * - Shows empty state when no conversation selected
 * - Integrates with WebSocket for real-time messages and typing indicators
 * - Auto-scrolls to bottom on new messages
 *
 * @example
 * ```tsx
 * <ChatPanel
 *   conversation={selectedConversation}
 *   isLoading={isLoadingConversation}
 *   onToggleDetails={toggleDetailsPanel}
 *   isDetailsOpen={isDetailsPanelOpen}
 * />
 * ```
 */
export function ChatPanelContainer({
  conversation,
  isLoading = false,
  onToggleDetails,
  isDetailsOpen = false,
  onForwardSuccess,
}: ChatPanelProps) {
  const translate = useTranslate();

  // Get current user ID from localStorage
  const currentUserId = Number(localStorage.getItem('userId')) || 0;

  // Reply/quote state
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);

  // Forward state
  const [forwardingMessage, setForwardingMessage] = useState<Message | null>(null);

  // Live conversation state: merges contact updates from WebSocket into the prop
  const [contactOverrides, setContactOverrides] = useState<{ name?: string; avatarFileId?: number | null } | null>(null);

  // Reset overrides, reply, and forward state when conversation changes
  useEffect(() => {
    setContactOverrides(null);
    setReplyingTo(null);
    setForwardingMessage(null);
  }, [conversation?.id]);

  // Listen for contact:updated WebSocket events to keep header in sync
  useEffect(() => {
    if (!conversation?.contact?.id) return;

    const handleContactUpdated = (payload: WsContactUpdatedPayload) => {
      if (payload.contactId !== conversation.contact!.id) return;
      setContactOverrides((prev) => ({
        ...prev,
        ...(payload.name !== undefined ? { name: payload.name } : {}),
        ...(payload.avatarFileId !== undefined ? { avatarFileId: payload.avatarFileId } : {}),
      }));
    };

    wsClient.on(ContactEvent.UPDATED, handleContactUpdated);
    return () => {
      wsClient.off(ContactEvent.UPDATED, handleContactUpdated);
    };
  }, [conversation?.contact?.id]);

  // Build the conversation with live contact data
  const liveConversation = useMemo(() => {
    if (!conversation || !contactOverrides) return conversation;
    return {
      ...conversation,
      contact: conversation.contact ? { ...conversation.contact, ...contactOverrides } : conversation.contact,
    };
  }, [conversation, contactOverrides]);

  // WebSocket is the source of truth for messages - it manages all real-time messages and typing
  const {
    messages: wsMessages,
    typing: typingUsers,
    reactions: realtimeReactions,
    clearMessages,
  } = useConversationSocket(conversation?.id as number | undefined);

  // Track if initial messages have been loaded from API
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Typing indicator for current user
  const { sendTyping, stopTyping } = useTypingIndicatorEnhanced(conversation?.id as number | undefined);

  // Send message hook
  const { sendMessage, isLoading: isSendingMessage } = useSendMessage();

  // Retry message hook
  const { retry: retryMessage } = useRetryMessage();

  // Handle sending a message
  const handleSendMessage = useCallback(
    async (payload: { body: string; attachments?: File[]; quotedMessageId?: number }) => {
      if (!conversation?.id) return;

      // Convert File[] to SendAttachment[] format expected by useSendMessage
      const attachments = payload.attachments?.map((file) => ({
        file,
        type: (file.type.startsWith('image/')
          ? 'image'
          : file.type.startsWith('video/')
            ? 'video'
            : file.type.startsWith('audio/')
              ? 'audio'
              : 'document') as 'image' | 'video' | 'audio' | 'document',
      }));

      // Send message — server will broadcast via WebSocket (chat:new-message)
      // which is our source of truth. No need to manually addMessage here;
      // doing so caused duplicates for file/image messages due to race conditions
      // between the HTTP response and the WebSocket event.
      await sendMessage(conversation.id as number, payload.body, attachments, payload.quotedMessageId);
    },
    [conversation?.id, sendMessage]
  );

  // Handle reply to a message
  const handleReply = useCallback((message: Message) => {
    setReplyingTo(message);
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyingTo(null);
  }, []);

  // Handle forward message
  const handleForward = useCallback((message: Message) => {
    setForwardingMessage(message);
  }, []);

  const handleCloseForward = useCallback(() => {
    setForwardingMessage(null);
  }, []);

  // Handle retrying a failed message
  const handleRetryMessage = useCallback(
    async (messageId: number) => {
      await retryMessage(messageId);
      // Status update will arrive via WebSocket (chat:message-status)
    },
    [retryMessage]
  );

  // Fetch initial messages from API ONCE on conversation load
  // This provides scrollback history. After this, WebSocket is the source of truth.
  const {
    data: infiniteData,
    isPending: isLoadingMessages,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteGetList<Message>(
    'message',
    {
      filter: { conversationId_eq: conversation?.id },
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'createdAt', order: 'ASC' },
    },
    { enabled: !!conversation?.id }
  );

  // Flatten pages from infinite list into a single array (for initial load)
  const apiMessages = useMemo(() => {
    if (!infiniteData?.pages) return [];
    return infiniteData.pages.flatMap((page) => page.data);
  }, [infiniteData]);

  // Mark initial load as complete when API finishes loading (even if empty)
  useEffect(() => {
    if (!isLoadingMessages && !initialLoadComplete && conversation?.id) {
      setInitialLoadComplete(true);
    }
  }, [isLoadingMessages, initialLoadComplete, conversation?.id]);

  // Reset initial load flag when conversation changes
  useEffect(() => {
    setInitialLoadComplete(false);
  }, [conversation?.id]);

  // Refetch API messages on WebSocket reconnection to catch any missed messages.
  // Note: useConversationSocket already resets its own WS messages on reconnect,
  // and WebSocketClient.resubscribeAll() handles re-subscription automatically.
  // We only need to trigger an API refetch here.
  useEffect(() => {
    if (!conversation?.id) return;

    const handleReconnect = () => {
      console.log('[ChatPanel] WebSocket reconnected, refetching messages from API');
      setInitialLoadComplete(false);
    };

    wsClient.on(ConnectionEvent.CONNECT, handleReconnect);

    return () => {
      wsClient.off(ConnectionEvent.CONNECT, handleReconnect);
    };
  }, [conversation?.id]);

  // Handle loading more messages (scrollback) - must be defined before early returns
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Messages to display: API messages for initial load + scrollback, WebSocket for new messages
  // WebSocket is the source of truth - it contains all messages received in real-time
  const allMessages = useMemo(() => {
    if (!initialLoadComplete) {
      // Still loading initial messages from API
      return apiMessages;
    }

    // After initial load, combine API messages (for scrollback) with WebSocket messages
    // Remove duplicates - WebSocket messages take precedence (use both id and uuid for matching)
    const wsMessageIds = new Set(wsMessages.map(m => m.id));
    const wsMessageUuids = new Set(wsMessages.filter(m => m.uuid).map(m => m.uuid));
    const uniqueApiMessages = apiMessages.filter(m =>
      !wsMessageIds.has(m.id) && (!m.uuid || !wsMessageUuids.has(m.uuid))
    );

    // Combine and sort by createdAt to maintain order
    const combined = [...uniqueApiMessages, ...wsMessages];
    combined.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    return combined;
  }, [apiMessages, wsMessages, initialLoadComplete]);

  // Extract channel type from conversation metadata
  const channelType = useMemo(() => {
    return ((conversation?.metadata as { provider?: string } | null)?.provider as ChannelType) ?? null;
  }, [conversation?.metadata]);

  // Determine file size limit and provider name based on channel provider
  const { maxFileSizeBytes, providerName } = useMemo(() => {
    if (channelType === ChannelType.TELEGRAM) {
      return { maxFileSizeBytes: 50 * 1024 * 1024, providerName: 'Telegram' }; // 50 MB
    }
    if (channelType === ChannelType.ZAPY) {
      return { maxFileSizeBytes: 100 * 1024 * 1024, providerName: 'WhatsApp' }; // 100 MB
    }
    return { maxFileSizeBytes: undefined, providerName: undefined };
  }, [channelType]);

  // Check if the ticket is closed - disable composer if so
  const isTicketClosed = useMemo(() => {
    if (!conversation?.tickets || conversation.tickets.length === 0) return false;

    // Check if there's an active (non-closed) ticket
    const hasActiveTicket = conversation.tickets.some(
      (ticket) => ticket.status === TicketStatus.PENDING ||
                  ticket.status === TicketStatus.OPEN ||
                  ticket.status === TicketStatus.RESOLVED
    );

    // If no active ticket, check if all tickets are closed
    if (!hasActiveTicket) {
      return conversation.tickets.every((ticket) => ticket.status === TicketStatus.CLOSED);
    }

    return false;
  }, [conversation?.tickets]);

  // Show loading state while conversation is being fetched
  if (isLoading) {
    return (
      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: { xs: 'calc(100vh - 64px)', md: 'calc(100vh - 100px)' },
        }}
      >
        <CircularProgress />
      </Paper>
    );
  }

  // Show empty state when no conversation is selected - with WhatsApp background
  if (!conversation) {
    return (
      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          display: 'flex',
          height: { xs: 'calc(100vh - 64px)', md: 'calc(100vh - 100px)' },
          overflow: 'hidden',
        }}
      >
        <EmptyConversationState
          title={translate('resources.conversations.empty.title')}
          subtitle={translate('resources.conversations.empty.subtitle')}
          icon={<InboxIcon sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.3 }} />}
        />
      </Paper>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <ChatPanelUI
        conversation={liveConversation!}
        messages={allMessages}
        currentUserId={currentUserId}
        typingUsers={typingUsers}
        isLoading={isLoadingMessages}
        hasMoreMessages={hasNextPage}
        onLoadMore={handleLoadMore}
        isLoadingMore={isFetchingNextPage}
        onToggleDetails={onToggleDetails}
        isDetailsOpen={isDetailsOpen}
        showDetailsButton={true}
        height="100%"
        showTicketActions={true}
        realtimeReactions={realtimeReactions}
        onRetryMessage={handleRetryMessage}
        onReply={handleReply}
        onForward={handleForward}
        messageComposer={
          <MessageComposer
            conversationUuid={conversation.uuid}
            onSendMessage={handleSendMessage}
            replyingTo={replyingTo}
            onCancelReply={handleCancelReply}
            onTyping={sendTyping}
            onStopTyping={stopTyping}
            disabled={isSendingMessage || isTicketClosed}
            maxFileSizeBytes={maxFileSizeBytes}
            providerName={providerName}
          />
        }
      />
      <ForwardMessageDialog
        open={!!forwardingMessage}
        onClose={handleCloseForward}
        message={forwardingMessage}
        channelConnectionId={conversation?.channelConnectionId ?? null}
        channelType={channelType}
        onSuccess={onForwardSuccess}
      />
    </Box>
  );
}
